import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserService } from 'app/core/user/user.service';
import { catchError, firstValueFrom, Observable, of, switchMap } from 'rxjs';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { environment } from 'environments/environment';
import jwtDecode from 'jwt-decode';

@Injectable()
export class AuthService {
    private _authenticated: boolean = false;
    // eslint-disable-next-line @typescript-eslint/member-ordering
    private api: string = environment.api;
    // private api: string = '/api';
    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        try {
            localStorage.setItem('accessToken', token);
        } catch (e) {
            console.log("Can't access local storage", e);
        }
    }

    get accessToken(): string {
        try {
            const accessToken =
                AuthUtils.getCookieFromStorage('jwt') ??
                sessionStorage.getItem('jwt');
            return accessToken;
        } catch {
            return '';
        }
    }

    getJwtCookie(): Promise<any> {
        return new Promise((resolve, reject) => {
            this._httpClient
                .get(`${this.api}/auth/roles`, {
                    withCredentials: true,
                })
                .pipe(
                    catchError((error: any): any => {
                        resolve(error);
                        throw new Error(error.message);
                    })
                )
                .subscribe((response) => {
                    resolve(response);
                });
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    refreshJwtToken(oldToken: string): Observable<any> {
        return this._httpClient.post(
            `${this.api}/auth/refresh`,
            {
                oldToken: oldToken,
            },
            {
                withCredentials: true,
            }
        );
    }
    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: {
        username: string;
        password: string;
    }): Observable<any> {
        return this._httpClient.post(`${this.api}/auth/signin`, credentials, {
            withCredentials: true,
        });
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Renew token
        return this._httpClient
            .post(`${this.api}/auth/refresh-access-token`, {
                accessToken: this.accessToken,
            })
            .pipe(
                catchError(() =>
                    // Return false
                    of(false)
                ),
                switchMap((response: any) => {
                    // Store the access token in the local storage
                    this.accessToken = response.accessToken;

                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    this._userService.user = response.user;

                    // Return true
                    return of(true);
                })
            );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage

        try {
            localStorage.removeItem('accessToken');
        } catch (e) {
            console.log(e);
        }

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: {
        name: string;
        email: string;
        password: string;
        company: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: {
        email: string;
        password: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            this._httpClient
                .get(`${this.api}/user/guard`, { withCredentials: true })
                .pipe(
                    catchError((error: any): any => {
                        resolve(false);
                        throw new Error(error.message);
                    })
                )
                .subscribe((response) => {
                    resolve(true);
                });
        });
    }

    getJwtUserInfo(): any {
        return jwtDecode(this.accessToken);
    }
}
